import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { schedulingApi } from "../../services/schedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { usePermissions } from "../../hooks/usePermissions";
import { EligibilityEmployeeList } from "../../components/scheduling/EligibilityEmployeeList";
import { ScheduleGrid } from "../../components/scheduling/ScheduleGrid";
import { SchedulingRightPane } from "../../components/scheduling/SchedulingRightPane";
import { MobileScheduleBuilder } from "../../components/scheduling/MobileScheduleBuilder";
import { QuickAssignDialog } from "../../components/scheduling/QuickAssignDialog";
import { ShiftDetailDialog } from "../../components/scheduling/ShiftDetailDialog";
import { useLocalization } from "../../hooks/useLocalization";

const UNDO_TOAST_MS = 6000;

function assignmentToPayload(a) {
  return {
    employee_id: a.employee_id, date: a.date, shift_template_id: a.shift_template_id || null,
    start_time: a.start_time, end_time: a.end_time, crosses_midnight: a.crosses_midnight || false,
    break_minutes: a.break_minutes || 0, role: a.role || null,
  };
}

export default function ScheduleBuilderPage() {
  const { scheduleId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const permissions = usePermissions();
  const { canEditSchedule, canApproveSchedule, canViewScheduling } = permissions;

  const [schedule, setSchedule] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [board, setBoard] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState("");

  const [checkedIds, setCheckedIds] = useState(new Set());
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const [highlightedEmployeeId, setHighlightedEmployeeId] = useState(null);
  const [quickAssignTarget, setQuickAssignTarget] = useState(null);
  const [shiftDetailTarget, setShiftDetailTarget] = useState(null);

  const reloadAll = async () => {
    const [scheduleResp, assignmentsResp, boardResp, coverageResp] = await Promise.all([
      schedulingApi.getSchedule(scheduleId), schedulingApi.listAssignments(scheduleId),
      schedulingApi.eligibilityBoard(scheduleId), schedulingApi.coverage(scheduleId),
    ]);
    setSchedule(scheduleResp.data);
    setAssignments(assignmentsResp.data);
    setBoard(boardResp.data);
    setCoverage(coverageResp.data);
  };

  useEffect(() => {
    if (!canViewScheduling) return;
    reloadAll().catch(() => setError("Failed to load schedule"));
    schedulingApi.listTemplates(true).then((r) => setTemplates(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, canViewScheduling]);

  // The eligibility board (scoped to this schedule's store) is the single source of truth for
  // employee names/positions in this workspace - it is a superset of `/employees/schedulable`
  // (which excludes some employees the schedule already has real assignments for), so every
  // name lookup here reads from it instead of a second, possibly-inconsistent employee list.
  const employees = useMemo(
    () => (board?.employees || []).map((e) => ({ id: e.employee_id, legal_name: e.legal_name, position: e.position })),
    [board],
  );

  // Default the center grid to whoever already has assignments in this schedule, once, the
  // first time the board loads - so the grid isn't empty on open, without forcing every
  // employee at this store into view at once.
  useEffect(() => {
    if (board && !defaultsApplied) {
      const withAssignments = board.employees.filter((e) => e.assigned_dates_in_this_schedule.length > 0).map((e) => e.employee_id);
      setCheckedIds(new Set(withAssignments));
      setDefaultsApplied(true);
    }
  }, [board, defaultsApplied]);

  const act = async (fn) => {
    setError("");
    try {
      await fn();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
      throw err;
    }
  };

  const toastUndo = (message, undoFn) => {
    toast.success(message, {
      duration: UNDO_TOAST_MS,
      action: {
        label: t("mgmtScheduling.undo"),
        onClick: () => undoFn().then(reloadAll).then(() => toast.success(t("mgmtScheduling.undone"))).catch(() => toast.error(t("mgmtScheduling.undoFailed"))),
      },
    });
  };

  const handleQuickAssignSubmit = ({ doubleShiftMode, form, secondForm, justification }) => act(async () => {
    const { employeeId, date } = quickAssignTarget;
    if (doubleShiftMode) {
      await schedulingApi.addDoubleShift(scheduleId, {
        employee_id: employeeId, date, manager_justification: justification,
        first: { ...form, employee_id: employeeId, date }, second: { ...secondForm, employee_id: employeeId, date },
      });
      toast.success(t("mgmtScheduling.doubleShiftAdded"));
    } else {
      const { data: created } = await schedulingApi.addAssignment(scheduleId, { ...form, employee_id: employeeId, date });
      toastUndo(t("mgmtScheduling.shiftAssigned"), () => schedulingApi.removeAssignment(created.id, "Undo of accidental assignment"));
    }
    await reloadAll();
  });

  const handleEditShift = (assignment, form, reason) => act(async () => {
    await schedulingApi.removeAssignment(assignment.id, `Edited: ${reason}`);
    const { data: created } = await schedulingApi.addAssignment(scheduleId, { ...assignmentToPayload(assignment), ...form });
    toastUndo(t("mgmtScheduling.shiftUpdated"), async () => {
      await schedulingApi.removeAssignment(created.id, "Undo of edit");
      await schedulingApi.addAssignment(scheduleId, assignmentToPayload(assignment));
    });
    await reloadAll();
  });

  const handleReplaceEmployee = (assignment, newEmployeeId, reason) => act(async () => {
    await schedulingApi.removeAssignment(assignment.id, `Replaced employee: ${reason}`);
    const { data: created } = await schedulingApi.addAssignment(scheduleId, { ...assignmentToPayload(assignment), employee_id: newEmployeeId });
    toastUndo(t("mgmtScheduling.employeeReplaced"), async () => {
      await schedulingApi.removeAssignment(created.id, "Undo of replace");
      await schedulingApi.addAssignment(scheduleId, assignmentToPayload(assignment));
    });
    await reloadAll();
  });

  const handleCopyShift = (assignment, targetEmployeeId, targetDate) => act(async () => {
    const { data: created } = await schedulingApi.addAssignment(scheduleId, { ...assignmentToPayload(assignment), employee_id: targetEmployeeId, date: targetDate });
    toastUndo(t("mgmtScheduling.shiftCopied"), () => schedulingApi.removeAssignment(created.id, "Undo of copy"));
    await reloadAll();
  });

  const handleRemoveShift = (assignment, reason) => act(async () => {
    await schedulingApi.removeAssignment(assignment.id, reason);
    toastUndo(t("mgmtScheduling.shiftRemoved"), () => schedulingApi.addAssignment(scheduleId, assignmentToPayload(assignment)));
    await reloadAll();
  });

  const handleDropMove = (assignmentId, newEmployeeId, newDate) => act(async () => {
    const original = assignments.find((a) => a.id === assignmentId);
    if (!original || (original.employee_id === newEmployeeId && original.date === newDate)) return;
    await schedulingApi.removeAssignment(original.id, "Moved via drag-and-drop");
    const { data: created } = await schedulingApi.addAssignment(scheduleId, { ...assignmentToPayload(original), employee_id: newEmployeeId, date: newDate });
    toastUndo(t("mgmtScheduling.shiftMoved"), async () => {
      await schedulingApi.removeAssignment(created.id, "Undo of move");
      await schedulingApi.addAssignment(scheduleId, assignmentToPayload(original));
    });
    await reloadAll();
  });

  const lifecycleActions = useMemo(() => ({
    validate: () => act(async () => { await schedulingApi.validateSchedule(scheduleId); toast.success(t("mgmtScheduling.scheduleValidated")); await reloadAll(); }),
    submitForReview: () => act(async () => { await schedulingApi.submitForReview(scheduleId); toast.success(t("mgmtScheduling.submittedForApproval")); await reloadAll(); }),
    approve: () => act(async () => { await schedulingApi.approveSchedule(scheduleId); toast.success(t("mgmtScheduling.scheduleApproved")); await reloadAll(); }),
    publish: () => act(async () => { await schedulingApi.publishSchedule(scheduleId); toast.success(t("mgmtScheduling.schedulePublished")); await reloadAll(); }),
    newVersion: () => act(async () => { const { data } = await schedulingApi.createNewVersion(scheduleId); navigate(`/management/scheduling/schedules/${data.id}`); }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [scheduleId]);

  const onToggleChecked = (id) => setCheckedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const onSelectEmployee = (id) => {
    setHighlightedEmployeeId(id);
    setCheckedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  };

  if (!canViewScheduling) return <p className="text-sm text-gray-500" data-testid="schedule-builder-access-denied">{t("common.accessDenied")}</p>;
  if (!schedule) return <p className="text-sm text-gray-500" data-testid="schedule-builder-loading">{t("common.loading")}</p>;

  const isDraftEditable = ["DRAFT", "VALIDATION_REQUIRED", "BLOCKED", "READY_FOR_REVIEW"].includes(schedule.status);

  return (
    <div className="flex flex-col" data-testid="schedule-builder-page">
      <PageHelpBanner pageKey="schedule-builder" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtScheduling.builderHelp") }} />
      </PageHelpBanner>
      <button onClick={() => navigate("/management/scheduling/schedules")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3" data-testid="back-to-schedules-link">
        <ArrowLeft size={15} /> {t("mgmtScheduling.backToSchedules")}
      </button>
      {error && <p className="text-sm text-red-500 mb-2" data-testid="schedule-builder-error">{error}</p>}

      {/* Desktop: true 3-pane workspace */}
      <div className="hidden lg:flex h-[calc(100vh-200px)] min-h-[560px] rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="w-72 shrink-0 border-r border-gray-100">
          <EligibilityEmployeeList board={board} checkedIds={checkedIds} highlightedId={highlightedEmployeeId} onToggleChecked={onToggleChecked} onSelectEmployee={onSelectEmployee} />
        </div>
        <div className="flex-1 min-w-0">
          <ScheduleGrid
            periodStart={schedule.period_start} periodEnd={schedule.period_end} assignments={assignments} board={board}
            checkedIds={checkedIds} highlightedEmployeeId={highlightedEmployeeId} templates={templates} isEditable={isDraftEditable && canEditSchedule}
            onEmptyCellClick={(employeeId, date) => setQuickAssignTarget({ employeeId, date })}
            onShiftClick={(a) => setShiftDetailTarget(a)}
            onDropAssign={(employeeId, date) => setQuickAssignTarget({ employeeId, date })}
            onDropMove={handleDropMove}
          />
        </div>
        <div className="w-80 shrink-0 border-l border-gray-100">
          <SchedulingRightPane schedule={schedule} coverage={coverage} employees={employees} permissions={permissions} actions={lifecycleActions} navigate={navigate} />
        </div>
      </div>

      {/* Mobile: a different workflow, not a shrunk 3-pane */}
      <div className="lg:hidden h-[calc(100vh-220px)] min-h-[480px] rounded-xl border border-gray-200 bg-white overflow-hidden">
        <MobileScheduleBuilder
          schedule={schedule} assignments={assignments} board={board} coverage={coverage} employees={employees} templates={templates}
          permissions={permissions} actions={lifecycleActions}
          onAssignEmployeeDate={(employeeId, date) => setQuickAssignTarget({ employeeId, date })}
          onShiftClick={(a) => setShiftDetailTarget(a)}
        />
      </div>

      <QuickAssignDialog
        open={!!quickAssignTarget} onOpenChange={(next) => !next && setQuickAssignTarget(null)}
        employeeName={employees.find((e) => e.id === quickAssignTarget?.employeeId)?.legal_name || ""}
        date={quickAssignTarget?.date || ""} templates={templates} onSubmit={handleQuickAssignSubmit}
      />
      <ShiftDetailDialog
        open={!!shiftDetailTarget} onOpenChange={(next) => !next && setShiftDetailTarget(null)}
        assignment={shiftDetailTarget} board={board} employees={employees} templates={templates}
        onEdit={handleEditShift} onReplace={handleReplaceEmployee} onCopy={handleCopyShift} onRemove={handleRemoveShift}
      />
    </div>
  );
}
